import { useState, type CSSProperties } from "react";
import { appUrl } from "@astro/config";
import { useSocket } from "./provider";
import { useSocketEvent } from "./hooks";

/** Payload of the BFF's consultation:invite relay (see auth-bff/realtime.ts). */
interface InvitePayload {
  bookingId?: string | number;
  meetingId?: string;
  fromUserId?: string | number;
  fromName?: string;
}

/**
 * Suite-wide listener for the astrologer's video-call invitation, for apps
 * that don't host the consultation room themselves (website, shop, …). The
 * jyotish app has its own richer modal (in-app router navigation); every
 * other app mounts this one so the seeker is reachable wherever they are —
 * accepting hands them off to the jyotish room with ?invited=1, which admits
 * them even before the booking status refetch catches up.
 *
 * Styling is inline on purpose: this renders inside any app of the suite, so
 * it cannot rely on a particular app's Tailwind sources or theme tokens.
 */
export function ConsultationInviteWatcher() {
  const [invite, setInvite] = useState<InvitePayload | null>(null);
  const { emit } = useSocket();

  useSocketEvent("consultation:invite", (payload) => {
    const data = payload as InvitePayload;
    if (data?.bookingId === undefined) return;
    setInvite(data);
  });

  // Astrologer withdrew (ended before the seeker answered).
  useSocketEvent("consultation:end", (payload) => {
    const data = payload as InvitePayload;
    setInvite((current) =>
      current && String(data?.bookingId) === String(current.bookingId) ? null : current
    );
  });

  if (!invite) return null;

  const accept = () => {
    emit("consultation:accept", { toUserId: invite.fromUserId, bookingId: invite.bookingId });
    setInvite(null);
    window.location.assign(appUrl("jyotish", `/consultations/${invite.bookingId}/video?invited=1`));
  };

  const decline = () => {
    emit("consultation:decline", { toUserId: invite.fromUserId, bookingId: invite.bookingId });
    setInvite(null);
  };

  return (
    <div style={styles.backdrop}>
      <div style={styles.card}>
        <div style={styles.accentBar} />
        <div style={{ padding: "2rem" }}>
          <div style={styles.icon}>📹</div>
          <p style={styles.title}>Your consultation is ready</p>
          <p style={styles.subtitle}>
            {invite.fromName || "Your astrologer"} is inviting you to join the video consultation
            {invite.bookingId ? ` for booking BKNG${invite.bookingId}` : ""}.
          </p>
          <div style={styles.actions}>
            <button type="button" onClick={accept} style={{ ...styles.button, ...styles.primary }}>
              Join Now
            </button>
            <button type="button" onClick={decline} style={{ ...styles.button, ...styles.secondary }}>
              Not Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 2147483000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.5rem",
    overflowY: "auto",
    background: "rgba(0,0,0,0.7)",
    backdropFilter: "blur(4px)",
  },
  card: {
    width: "100%",
    maxWidth: "28rem",
    margin: "auto",
    borderRadius: "1rem",
    overflow: "hidden",
    background: "#1F1913",
    border: "1px solid rgba(200,146,42,0.3)",
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
    textAlign: "center",
    fontFamily: "inherit",
  },
  accentBar: {
    height: "4px",
    width: "100%",
    background: "linear-gradient(to right, #C8922A, #E6C878, #C8922A)",
  },
  icon: {
    margin: "0 auto",
    width: "4rem",
    height: "4rem",
    borderRadius: "9999px",
    background: "linear-gradient(to top right, #C8922A, #E6C878)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.875rem",
  },
  title: {
    fontSize: "1.25rem",
    color: "#fff",
    marginTop: "1.25rem",
    fontWeight: 600,
  },
  subtitle: {
    fontSize: "0.875rem",
    color: "rgba(255,255,255,0.5)",
    marginTop: "0.5rem",
    lineHeight: 1.5,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    marginTop: "1.75rem",
  },
  button: {
    borderRadius: "9999px",
    padding: "0.875rem 2rem",
    fontSize: "0.75rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.2em",
    cursor: "pointer",
  },
  primary: {
    background: "linear-gradient(to right, #C8922A, #E6C878)",
    color: "#1C140A",
    border: "none",
  },
  secondary: {
    background: "transparent",
    color: "rgba(255,255,255,0.6)",
    border: "1px solid rgba(255,255,255,0.2)",
  },
};
